import { useQuery } from "@tanstack/solid-query";
import { EVAL_BATCH_STATUS } from "contracts/enums/evals";
import { api } from "../../store/api";
import { RUN_POLL_MS } from "../../store/pollCadence";
import { queryClient } from "../../store/query";
import {
  type ComparisonRead,
  comparisonNotReady,
  comparisonStillMoving,
} from "./comparisonState";

/**
 * Every eval query key, poll cadence and invalidation, mirroring
 * `features/playground/playgroundQueries.ts`. Components import these rather
 * than hand-writing key arrays, so the cache namespaces cannot drift.
 *
 * Both waits poll at the run cadence, because both are waiting on the same
 * thing underneath: a crawl run finishing.
 */

const suitesKey = () => ["eval-suites"] as const;
const batchesKey = (suiteId: string | null) =>
  ["eval-batches", suiteId ?? "all"] as const;
const batchDetailKey = (batchId: string) =>
  ["eval-batch-detail", batchId] as const;
const comparisonKey = (comparisonId: string) =>
  ["eval-comparison", comparisonId] as const;

async function readComparison(comparisonId: string): Promise<ComparisonRead> {
  try {
    return {
      ready: true,
      detail: await api.evals.comparisonDetail(comparisonId),
    };
  } catch (cause) {
    if (comparisonNotReady(cause)) return { ready: false };
    throw cause;
  }
}

export function createEvalSuites() {
  return useQuery(() => ({
    queryKey: suitesKey(),
    queryFn: () => api.evals.listSuites(),
  }));
}

export function createEvalBatches(suiteId: () => string | null) {
  return useQuery(() => ({
    queryKey: batchesKey(suiteId()),
    queryFn: () => api.evals.listBatches(suiteId()),
    refetchInterval: (query) =>
      query.state.data?.some(
        (batch) => batch.status === EVAL_BATCH_STATUS.RUNNING,
      )
        ? RUN_POLL_MS
        : false,
  }));
}

export function createEvalBatchDetail(batchId: () => string) {
  return useQuery(() => ({
    queryKey: batchDetailKey(batchId()),
    queryFn: () => api.evals.batchDetail(batchId()),
    refetchInterval: (query) =>
      query.state.data?.status === EVAL_BATCH_STATUS.RUNNING
        ? RUN_POLL_MS
        : false,
  }));
}

export function createEvalComparison(comparisonId: () => string) {
  return useQuery(() => ({
    queryKey: comparisonKey(comparisonId()),
    queryFn: () => readComparison(comparisonId()),
    // Readiness is not the end of the wait — the judge still has to write a
    // verdict. `comparisonStillMoving` owns that condition.
    refetchInterval: (query) =>
      comparisonStillMoving(query.state.data) ? RUN_POLL_MS : false,
  }));
}

export const invalidateEvalSuites = () =>
  queryClient.invalidateQueries({ queryKey: suitesKey() });
export const invalidateEvalBatches = (suiteId: string | null) =>
  queryClient.invalidateQueries({ queryKey: batchesKey(suiteId) });
export const invalidateEvalComparison = (comparisonId: string) =>
  queryClient.invalidateQueries({ queryKey: comparisonKey(comparisonId) });
